import React, { useState, useEffect } from "react";

interface ArticleData {
  id: number;
  title: string;
  applicationDateFrom: string | null;
  applicationDateTo: string | null;
  isBur: boolean;
  isContinuous: boolean;
  isAloc: boolean;
  targetOverride: string | null;
  operatorTarget: string | null;
  operatorId: number;
  operatorName: string | null;
}

interface OperatorOption {
  operatorId: number;
  operatorName: string | null;
  projectName: string | null;
  voivodeship: string;
}

const TARGET_OPTIONS = [
  { value: "", label: "Użyj kategorii operatora" },
  { value: "FIRMY", label: "Pracodawcy" },
  { value: "DOROSLI", label: "Osoby dorosłe" },
  { value: "DOROSLI_TRANSFORMACJA", label: "Zielona transformacja" },
];

interface EditBurArticleFlagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    applicationDateFrom: string | null;
    applicationDateTo: string | null;
    isBur: boolean;
    isContinuous: boolean;
    isAloc: boolean;
    targetOverride: string | null;
    operatorId?: number;
  }) => Promise<void>;
  article: ArticleData | null;
  operators?: OperatorOption[];
}

const EditBurArticleFlagsModal: React.FC<EditBurArticleFlagsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  article,
  operators,
}) => {
  const [applicationDateFrom, setApplicationDateFrom] = useState<string>("");
  const [applicationDateTo, setApplicationDateTo] = useState<string>("");
  const [isBur, setIsBur] = useState(false);
  const [isContinuous, setIsContinuous] = useState(false);
  const [isAloc, setIsAloc] = useState(false);
  const [targetOverride, setTargetOverride] = useState<string>("");
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (article && isOpen) {
      setApplicationDateFrom(
        article.applicationDateFrom
          ? article.applicationDateFrom.split("T")[0]
          : ""
      );
      setApplicationDateTo(
        article.applicationDateTo
          ? article.applicationDateTo.split("T")[0]
          : ""
      );
      setIsBur(article.isBur);
      setIsContinuous(article.isContinuous);
      setIsAloc(article.isAloc);
      setTargetOverride(article.targetOverride || "");
      setSelectedOperatorId(article.operatorId);
    }
  }, [article, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const data: {
        applicationDateFrom: string | null;
        applicationDateTo: string | null;
        isBur: boolean;
        isContinuous: boolean;
        isAloc: boolean;
        targetOverride: string | null;
        operatorId?: number;
      } = {
        applicationDateFrom: applicationDateFrom || null,
        applicationDateTo: applicationDateTo || null,
        isBur,
        isContinuous,
        isAloc,
        targetOverride: targetOverride || null,
      };

      // Only include operatorId if it changed
      if (selectedOperatorId && selectedOperatorId !== article?.operatorId) {
        data.operatorId = selectedOperatorId;
      }

      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error("Failed to update article flags:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !article) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            Edytuj flagi i daty naboru BUR
          </h2>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Artykul:</p>
            <p className="text-sm font-medium text-gray-900 line-clamp-2">
              {article.title}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="applicationDateFrom"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Data rozpoczecia naboru
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  id="applicationDateFrom"
                  value={applicationDateFrom}
                  onChange={(e) => setApplicationDateFrom(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setApplicationDateFrom("")}
                  disabled={isSubmitting || !applicationDateFrom}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  title="Wyczysc date"
                >
                  Wyczysc
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="applicationDateTo"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Data zakonczenia naboru
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  id="applicationDateTo"
                  value={applicationDateTo}
                  onChange={(e) => setApplicationDateTo(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setApplicationDateTo("")}
                  disabled={isSubmitting || !applicationDateTo}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  title="Wyczysc date"
                >
                  Wyczysc
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isBur}
                  onChange={(e) => setIsBur(e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Flaga BUR (isBur)
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-7 mt-1">
                Oznacza artykul jako nabor BUR
              </p>
            </div>

            <div className="mb-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isContinuous}
                  onChange={(e) => setIsContinuous(e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Nabor ciagly (isContinuous)
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-7 mt-1">
                Nabor bez okreslonej daty zakonczenia
              </p>
            </div>

            <div className="mb-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isAloc}
                  onChange={(e) => setIsAloc(e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Alokacyjny (isAloc)
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-7 mt-1">
                Nabor z alokacja srodkow
              </p>
            </div>

            {operators && operators.length > 0 && (
              <div className="mb-4">
                <label
                  htmlFor="operatorId"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Operator / Projekt
                </label>
                <select
                  id="operatorId"
                  value={selectedOperatorId || ""}
                  onChange={(e) =>
                    setSelectedOperatorId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {operators.map((op) => (
                    <option key={op.operatorId} value={op.operatorId}>
                      {op.operatorName || op.projectName || `Operator ${op.operatorId}`}
                      {op.projectName && op.operatorName ? ` - ${op.projectName}` : ""}
                      {" "}({op.voivodeship})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Zmień przypisanie do innego operatora/projektu
                </p>
              </div>
            )}

            <div className="mb-6">
              <label
                htmlFor="targetOverride"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Kategoria
              </label>
              <select
                id="targetOverride"
                value={targetOverride}
                onChange={(e) => setTargetOverride(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {TARGET_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                    {option.value === "" && article?.operatorTarget
                      ? ` (${
                          TARGET_OPTIONS.find(
                            (o) => o.value === article.operatorTarget
                          )?.label || article.operatorTarget
                        })`
                      : ""}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Nadpisuje kategorie z operatora dla tego naboru
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-700 border border-transparent rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Zapisywanie..." : "Zapisz zmiany"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditBurArticleFlagsModal;
